import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, MapPin, Waypoints, Ruler, Check, X, AlertTriangle, Zap } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Spinner } from "@/components/tac";

// Interactive blueprint canvas. Point pins (normalized 0..1) are draggable DOM
// markers. Wire/pipe "runs" are drawable polylines rendered in an SVG overlay;
// their footage is derived from a user-drawn calibration line (one known length).
export default function PinCanvas({ blueprintId, catalog, initialPins, initialRuns, initialScale, clashes, contentType, onSaved }) {
  const boxRef = useRef(null);
  const dragPin = useRef(null);
  const dragVertex = useRef(null);
  const movedRef = useRef(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aspect, setAspect] = useState(initialScale?.aspect || 1);
  const [pins, setPins] = useState(initialPins || []);
  const [runs, setRuns] = useState(initialRuns || []);
  const [scale, setScale] = useState(initialScale || null);
  const [mode, setMode] = useState("pin"); // pin | run | calibrate
  const [trade, setTrade] = useState("electrical");
  const [type, setType] = useState("outlet");
  const [selKind, setSelKind] = useState(null); // pin | run
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState([]); // in-progress run vertices
  const [calDraft, setCalDraft] = useState([]); // calibration line points
  const [snapTarget, setSnapTarget] = useState(null); // {x,y} a run point would snap to
  const [autoPick, setAutoPick] = useState(null); // autowire source pin
  const [calFt, setCalFt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    setPins(initialPins || []); setRuns(initialRuns || []); setScale(initialScale || null);
    setAspect(initialScale?.aspect || 1);
    setSelId(null); setSelKind(null); setDraft([]); setCalDraft([]);
    api.get(`/blueprints/${blueprintId}/file`).then((r) => setDataUrl(r.data.data_url)).catch(() => setDataUrl(null)).finally(() => setLoading(false));
  }, [blueprintId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPdf = (contentType || "").includes("pdf");
  const color = (t) => catalog?.[t]?.color || "#F59E0B";
  const typeLabel = (t, ty) => catalog?.[t]?.types?.[ty]?.label || ty;
  const isLinear = (t, ty) => !!catalog?.[t]?.types?.[ty]?.linear;
  const typesFor = (t, linear) => Object.entries(catalog?.[t]?.types || {}).filter(([, m]) => !!m.linear === linear);

  // Length helpers: aspect-corrected normalized metric, then feet via calibration.
  const segLen = (p1, p2) => Math.hypot(p2.x - p1.x, (p2.y - p1.y) / aspect);
  const polyLen = (pts) => pts.reduce((s, p, i) => (i ? s + segLen(pts[i - 1], p) : 0), 0);
  const ftPerUnit = scale?.ft_per_unit || null;
  const runFt = (r) => (ftPerUnit ? polyLen(r.points) * ftPerUnit : null);
  const fmtFt = (v) => (v == null ? "-" : `${v.toFixed(1)} ft`);

  // Snap a run point to the nearest pin OR onto an existing run line within threshold.
  const SNAP = 0.02;
  const projSeg = (p, a, b) => {
    const ax = a.x, ay = a.y / aspect, bx = b.x, by = b.y / aspect, px = p.x, py = p.y / aspect;
    const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0; t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    return { x: cx, y: cy * aspect, dist: Math.hypot(px - cx, py - cy) };
  };
  const nearestSnap = (pt, excludeRunId) => {
    let best = null, bd = SNAP;
    for (const p of pins) { const d = Math.hypot(p.x - pt.x, (p.y - pt.y) / aspect); if (d < bd) { bd = d; best = { x: p.x, y: p.y }; } }
    for (const r of runs) {
      if (r.id === excludeRunId) continue;
      for (let i = 1; i < r.points.length; i++) { const pr = projSeg(pt, r.points[i - 1], r.points[i]); if (pr.dist < bd) { bd = pr.dist; best = { x: pr.x, y: pr.y }; } }
    }
    return best;
  };
  const snap = (pt, excludeRunId) => nearestSnap(pt, excludeRunId) || pt;

  const autowire = (pin) => {
    if (!type) { toast.error("Pick a run type first"); return; }
    if (!autoPick) { setAutoPick(pin); return; }
    if (autoPick.id === pin.id) { setAutoPick(null); return; }
    const run = { id: `tmp-${Date.now()}`, trade, type, points: [{ x: autoPick.x, y: autoPick.y }, { x: pin.x, y: pin.y }], label: "" };
    setRuns((r) => [...r, run]); setAutoPick(null); setSelKind("run"); setSelId(run.id);
    toast.success("Home-run drawn");
  };

  const switchMode = (m) => {
    setMode(m); setSelId(null); setSelKind(null); setDraft([]); setSnapTarget(null); setAutoPick(null);
    if (m === "run" || m === "autowire") setType((typesFor(trade, true)[0] || [])[0] || "");
    if (m === "pin") setType((typesFor(trade, false)[0] || [])[0] || "");
    if (m !== "calibrate") setCalDraft([]);
  };
  const pickTrade = (t) => {
    setTrade(t);
    setType((typesFor(t, mode === "run" || mode === "autowire")[0] || [])[0] || "");
  };

  const relXY = (e) => {
    const rect = boxRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  };

  const onCanvasClick = (e) => {
    if (dragPin.current || dragVertex.current || isPdf) return;
    if (movedRef.current) return;
    const { x, y } = relXY(e);
    if (mode === "pin") {
      if (!type) return;
      const pin = { id: `tmp-${Date.now()}`, trade, type, x, y, label: "", notes: "" };
      setPins((p) => [...p, pin]); setSelKind("pin"); setSelId(pin.id);
    } else if (mode === "run") {
      if (!type) return;
      setDraft((d) => [...d, snap({ x, y })]);
    } else if (mode === "calibrate") {
      setCalDraft((d) => (d.length >= 2 ? [{ x, y }] : [...d, { x, y }]));
    }
  };

  const finishRun = () => {
    if (draft.length < 2) { toast.error("Add at least two points"); return; }
    const run = { id: `tmp-${Date.now()}`, trade, type, points: draft, label: "" };
    setRuns((r) => [...r, run]); setDraft([]); setSelKind("run"); setSelId(run.id);
  };

  const applyCalibration = () => {
    const ft = parseFloat(calFt);
    if (calDraft.length !== 2 || !ft || ft <= 0) { toast.error("Draw a line and enter its real length in feet"); return; }
    const units = segLen(calDraft[0], calDraft[1]);
    if (units <= 0) { toast.error("Calibration line is too short"); return; }
    setScale({ ft_per_unit: ft / units, aspect, cal_points: calDraft, cal_ft: ft });
    toast.success(`Scale set: ${(ft / units).toFixed(1)} ft/unit`);
    switchMode("run");
  };

  const onMove = (e) => {
    if (dragPin.current) {
      movedRef.current = true;
      const { x, y } = relXY(e);
      setPins((ps) => ps.map((p) => (p.id === dragPin.current ? { ...p, x, y } : p)));
    } else if (dragVertex.current) {
      movedRef.current = true;
      const { runId, idx } = dragVertex.current;
      const s = snap(relXY(e), runId);
      setSnapTarget(nearestSnap(relXY(e), runId));
      setRuns((rs) => rs.map((r) => (r.id === runId ? { ...r, points: r.points.map((pt, i) => (i === idx ? s : pt)) } : r)));
    } else if (mode === "run" && !isPdf) {
      setSnapTarget(nearestSnap(relXY(e), null));
    }
  };
  const endDrag = () => { dragPin.current = null; dragVertex.current = null; setSnapTarget(null); setTimeout(() => { movedRef.current = false; }, 0); };

  const updateSel = (patch) => {
    if (selKind === "pin") setPins((ps) => ps.map((p) => (p.id === selId ? { ...p, ...patch } : p)));
    else setRuns((rs) => rs.map((r) => (r.id === selId ? { ...r, ...patch } : r)));
  };
  const removeSel = () => {
    if (selKind === "pin") setPins((ps) => ps.filter((p) => p.id !== selId));
    else setRuns((rs) => rs.filter((r) => r.id !== selId));
    setSelId(null); setSelKind(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        pins: pins.map((p) => ({ id: p.id.startsWith("tmp-") ? null : p.id, trade: p.trade, type: p.type, x: p.x, y: p.y, label: p.label, notes: p.notes })),
        runs: runs.map((r) => ({ id: r.id.startsWith("tmp-") ? null : r.id, trade: r.trade, type: r.type, points: r.points.map((pt) => ({ x: pt.x, y: pt.y })), length_ft: runFt(r), label: r.label })),
        scale: scale ? { ft_per_unit: scale.ft_per_unit, aspect, cal_points: scale.cal_points, cal_ft: scale.cal_ft } : null,
      };
      const { data } = await api.put(`/blueprints/${blueprintId}/pins`, payload);
      toast.success(`${data.count} pins · ${data.run_count} runs saved`);
      onSaved?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setSaving(false);
  };

  const sel = selKind === "pin" ? pins.find((p) => p.id === selId) : runs.find((r) => r.id === selId);
  const paletteTypes = typesFor(trade, mode === "run" || mode === "autowire");
  const bpClashes = (clashes || []).filter((c) => c.blueprint_id === blueprintId);

  const MODES = [
    { key: "pin", label: "Pins", icon: MapPin },
    { key: "run", label: "Runs", icon: Waypoints },
    { key: "autowire", label: "Autowire", icon: Zap },
    { key: "calibrate", label: "Calibrate", icon: Ruler },
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
      <div>
        {/* Mode toggle */}
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="canvas-mode">
          {MODES.map((m) => (
            <button key={m.key} data-testid={`mode-${m.key}`} onClick={() => switchMode(m.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${mode === m.key ? "bg-cyan-500 text-black" : "border border-slate-700 text-slate-300 hover:text-white"}`}>
              <m.icon className="h-3.5 w-3.5" /> {m.label}
            </button>
          ))}
          {ftPerUnit && <span className="font-mono-tac text-[10px] text-emerald-400" data-testid="scale-badge">scale set · {scale.cal_ft} ft ref</span>}
          {!ftPerUnit && <span className="font-mono-tac text-[10px] text-slate-500">no scale · runs need calibration for feet</span>}
        </div>

        {/* Trade + type palette (pin/run modes) */}
        {mode !== "calibrate" && (<>
          <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="pin-palette">
            {Object.entries(catalog || {}).map(([t, info]) => (
              <button key={t} data-testid={`trade-${t}`} onClick={() => pickTrade(t)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${trade === t ? "text-black" : "text-slate-300 border border-slate-700"}`}
                style={trade === t ? { background: info.color } : {}}>{info.label}</button>
            ))}
          </div>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {paletteTypes.map(([ty, meta]) => (
              <button key={ty} data-testid={`ptype-${ty}`} onClick={() => setType(ty)}
                className={`rounded-lg px-2.5 py-1 text-[11px] transition-colors ${type === ty ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "border border-slate-800 text-slate-400 hover:text-white"}`}>{meta.label}</button>
            ))}
          </div>
        </>)}

        {/* Run draw controls */}
        {mode === "run" && !isPdf && (
          <div className="mb-3 flex items-center gap-2 text-xs text-slate-400" data-testid="run-controls">
            <span className="font-mono-tac text-[11px]">points: {draft.length}{draft.length >= 2 && ftPerUnit ? ` · ${fmtFt(polyLen(draft) * ftPerUnit)}` : ""}</span>
            <button data-testid="finish-run-btn" disabled={draft.length < 2} onClick={finishRun} className="inline-flex items-center gap-1 rounded-lg border border-emerald-600 px-2.5 py-1 text-emerald-300 disabled:opacity-40"><Check className="h-3 w-3" /> Finish run</button>
            <button data-testid="cancel-run-btn" disabled={!draft.length} onClick={() => setDraft([])} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1 text-slate-400 disabled:opacity-40"><X className="h-3 w-3" /> Clear</button>
          </div>
        )}

        {/* Autowire controls */}
        {mode === "autowire" && !isPdf && (
          <div className="mb-3 flex items-center gap-2 text-xs text-slate-400" data-testid="autowire-controls">
            <span className="font-mono-tac text-[11px]">{autoPick ? "now click the device pin" : "click the source pin (e.g. panel)"}</span>
            {autoPick && <button data-testid="autowire-cancel-btn" onClick={() => setAutoPick(null)} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1 text-slate-400"><X className="h-3 w-3" /> Cancel</button>}
          </div>
        )}

        {/* Calibrate controls */}
        {mode === "calibrate" && !isPdf && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-[#0d1420] p-3 text-xs" data-testid="calibrate-controls">
            <span className="text-slate-400">Click a start and end point on a known dimension, then enter its real length.</span>
            <input data-testid="cal-ft-input" type="number" min="0" step="0.5" value={calFt} onChange={(e) => setCalFt(e.target.value)} placeholder="feet" className="w-24 rounded-lg border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white focus:border-cyan-500 focus:outline-none" />
            <button data-testid="set-scale-btn" disabled={calDraft.length !== 2} onClick={applyCalibration} className="inline-flex items-center gap-1 rounded-lg border border-cyan-600 px-2.5 py-1 text-cyan-300 disabled:opacity-40"><Ruler className="h-3 w-3" /> Set scale</button>
            {calDraft.length > 0 && <button onClick={() => setCalDraft([])} className="text-slate-500 hover:text-white">reset</button>}
          </div>
        )}

        {/* Canvas */}
        <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-[#0d1420]" style={{ minHeight: 320 }}>
          {loading ? <div className="grid h-80 place-items-center"><Spinner label="Loading blueprint" /></div> : isPdf ? (
            <div className="grid h-80 place-items-center p-6 text-center text-sm text-slate-400" data-testid="pdf-notice">PDF blueprints are stored but pinning needs an image (PNG/JPG). Upload an image export to pin on it.</div>
          ) : (
            <div ref={boxRef} onClick={onCanvasClick} onPointerMove={onMove} onPointerUp={endDrag} onPointerLeave={endDrag}
              className={`relative w-full select-none ${mode === "pin" ? "cursor-crosshair" : "cursor-copy"}`} data-testid="pin-canvas">
              <img src={dataUrl} alt="blueprint" className="pointer-events-none w-full" draggable={false}
                onLoad={(e) => { const a = e.target.naturalWidth / e.target.naturalHeight; if (a) setAspect(a); }} />

              {/* SVG overlay: lines only. Dots/handles are DOM so they stay small + circular. */}
              <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full">
                {runs.map((r) => (
                  <polyline key={r.id} data-testid={`run-${r.id}`} points={r.points.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none" stroke={color(r.trade)} strokeWidth={selId === r.id ? 4 : 2.5} strokeLinejoin="round" strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: mode === "pin" ? "stroke" : "none", cursor: "pointer", opacity: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); setSelKind("run"); setSelId(r.id); }} />
                ))}
                {draft.length > 0 && (
                  <polyline points={draft.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke={color(trade)}
                    strokeWidth={2.5} strokeDasharray="0.02 0.015" vectorEffect="non-scaling-stroke" style={{ opacity: 0.85 }} />
                )}
                {calDraft.length === 2 && (
                  <polyline points={calDraft.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#22D3EE" strokeWidth={2.5} strokeDasharray="0.03 0.015" vectorEffect="non-scaling-stroke" />
                )}
                {bpClashes.map((c, i) => (
                  <line key={`cl-${i}`} data-testid={`clash-line-${i}`} x1={c.a.x} y1={c.a.y} x2={c.b.x} y2={c.b.y}
                    stroke={c.severity === "high" ? "#FB7185" : "#FBBF24"} strokeWidth={2} strokeDasharray="0.015 0.01" vectorEffect="non-scaling-stroke"
                    style={{ opacity: (c.status || "open") === "resolved" ? 0.2 : (c.status || "open") === "reviewed" ? 0.55 : 0.95 }} />
                ))}
              </svg>

              {/* Draggable vertex handles for the selected run */}
              {selKind === "run" && (runs.find((r) => r.id === selId)?.points || []).map((p, i) => (
                <span key={`vh-${i}`} data-testid={`run-vertex-${i}`}
                  onPointerDown={(e) => { e.stopPropagation(); dragVertex.current = { runId: selId, idx: i }; }}
                  className="absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: 13, height: 13, background: color(runs.find((r) => r.id === selId).trade), cursor: "grab" }} />
              ))}
              {/* Draft + calibration point markers */}
              {draft.map((p, i) => (
                <span key={`df-${i}`} className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/70"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: 9, height: 9, background: color(trade) }} />
              ))}
              {calDraft.map((p, i) => (
                <span key={`cal-${i}`} className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: 10, height: 10, background: "#22D3EE" }} />
              ))}
              {/* Pulsing clash hotspots */}
              {bpClashes.map((c, i) => [["a", c.a], ["b", c.b]].map(([k, pt]) => {
                const st = c.status || "open";
                if (st === "resolved") return null;
                const cc = c.severity === "high" ? "#FB7185" : "#FBBF24";
                const open = st === "open";
                return (
                  <span key={`ch-${i}-${k}`} data-testid={`clash-hotspot-${i}-${k}`}
                    className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%`, width: 18, height: 18, border: `2px solid ${cc}`, opacity: open ? 1 : 0.5 }}>
                    {open && <span className="absolute inset-0 rounded-full animate-ping" style={{ border: `2px solid ${cc}` }} />}
                  </span>
                );
              }))}

              {/* Live snap-target glow: ring at the pin OR run-line point a run vertex would snap to */}
              {snapTarget && (
                <span data-testid="snap-glow" className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ left: `${snapTarget.x * 100}%`, top: `${snapTarget.y * 100}%`, width: 28, height: 28, border: "2px solid #22D3EE", boxShadow: "0 0 12px 2px rgba(34,211,238,0.75)" }}>
                  <span className="absolute inset-0 rounded-full animate-ping" style={{ border: "2px solid #22D3EE" }} />
                </span>
              )}
              {/* Autowire source highlight */}
              {mode === "autowire" && autoPick && (
                <span data-testid="autowire-source" className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ left: `${autoPick.x * 100}%`, top: `${autoPick.y * 100}%`, width: 26, height: 26, border: "2px solid #F59E0B", boxShadow: "0 0 10px 2px rgba(245,158,11,0.7)" }} />
              )}

              {/* Point pins */}
              {pins.map((p) => (
                <button key={p.id} data-testid={`pin-${p.id}`}
                  onPointerDown={(e) => { if (mode !== "pin") return; e.stopPropagation(); dragPin.current = p.id; setSelKind("pin"); setSelId(p.id); }}
                  onClick={(e) => { e.stopPropagation(); if (mode === "autowire") { autowire(p); return; } if (!movedRef.current) { setSelKind("pin"); setSelId(p.id); } }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: selId === p.id ? 20 : 15, height: selId === p.id ? 20 : 15, background: color(p.trade), pointerEvents: mode === "pin" || mode === "autowire" ? "auto" : "none", cursor: mode === "autowire" ? "pointer" : "grab" }}
                  title={`${typeLabel(p.trade, p.type)}${p.label ? " · " + p.label : ""}`} />
              ))}
            </div>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="font-mono-tac text-[11px] text-slate-500">
            {isPdf ? "" : mode === "pin" ? "Click to drop a pin · drag to reposition"
              : mode === "run" ? "Click to add run points · snaps to pins & runs · Finish when done"
                : mode === "autowire" ? "Click a source pin then a device pin to auto-draw the run"
                  : "Click two ends of a known dimension"}
          </p>
          <button data-testid="save-pins-btn" disabled={saving || isPdf} onClick={save} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs disabled:opacity-50"><Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save plan"}</button>
        </div>
      </div>

      {/* Selection editor */}
      <div className="card-tac h-fit p-4" data-testid="pin-editor">
        {selKind === "pin" && sel ? (
          <>
            <div className="mb-2 flex items-center gap-2 text-sm text-white"><span className="h-3 w-3 rounded-full" style={{ background: color(sel.trade) }} /> {typeLabel(sel.trade, sel.type)}</div>
            <label className="mb-1 block font-mono-tac text-[10px] uppercase text-slate-500">Label</label>
            <input data-testid="pin-label-input" value={sel.label || ""} onChange={(e) => updateSel({ label: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" placeholder="e.g. Kitchen island" />
            <label className="mb-1 block font-mono-tac text-[10px] uppercase text-slate-500">Notes</label>
            <textarea data-testid="pin-notes-input" value={sel.notes || ""} onChange={(e) => updateSel({ notes: e.target.value })} rows={3} className="mb-3 w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" placeholder="Rough-in note" />
            <button data-testid="pin-delete-btn" onClick={removeSel} className="inline-flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /> Delete pin</button>
          </>
        ) : selKind === "run" && sel ? (
          <>
            <div className="mb-2 flex items-center gap-2 text-sm text-white"><span className="h-3 w-1 rounded" style={{ background: color(sel.trade) }} /> {typeLabel(sel.trade, sel.type)}</div>
            <div className="mb-3 rounded-lg border border-slate-800 bg-[#0d1420] p-3">
              <div className="flex items-baseline justify-between"><span className="font-mono-tac text-[10px] uppercase text-slate-500">Length</span>
                <span className="font-display text-xl font-bold text-white" data-testid="run-length">{fmtFt(runFt(sel))}</span></div>
              {!ftPerUnit && <p className="mt-1 text-[11px] text-amber-400">Calibrate to get feet.</p>}
              <p className="mt-1 font-mono-tac text-[10px] text-slate-600">{sel.points.length} points</p>
            </div>
            <label className="mb-1 block font-mono-tac text-[10px] uppercase text-slate-500">Label</label>
            <input data-testid="run-label-input" value={sel.label || ""} onChange={(e) => updateSel({ label: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" placeholder="e.g. Master bath home run" />
            <button data-testid="run-delete-btn" onClick={removeSel} className="inline-flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /> Delete run</button>
          </>
        ) : mode === "calibrate" ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-xs text-slate-500"><Ruler className="h-6 w-6 text-slate-700" /> Draw a line on a wall or dimension you know the length of, then enter it to set the plan scale.</div>
        ) : bpClashes.length ? (
          <div data-testid="clash-side-panel">
            <div className="mb-2 flex items-center gap-2 font-mono-tac text-[10px] uppercase text-rose-400"><AlertTriangle className="h-3.5 w-3.5" /> Clashes on this sheet ({bpClashes.length})</div>
            {bpClashes.slice(0, 8).map((c, i) => (
              <div key={i} className="border-b border-slate-800 py-1 text-[11px] text-slate-400">
                <span className={c.severity === "high" ? "text-rose-400" : "text-amber-400"}>●</span> {c.a.trade}/{c.a.type} ✕ {c.b.trade}/{c.b.type}
                {c.status && c.status !== "open" && <span className="ml-1 text-slate-600">· {c.status}</span>}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-xs text-slate-500"><MapPin className="h-6 w-6 text-slate-700" /> Pick a mode: drop pins, draw wire/pipe runs, or calibrate the scale.</div>
        )}
      </div>
    </div>
  );
}
